import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  TextField,
  Typography,
  Chip,
  Button,
  LinearProgress,
  Collapse,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import TableRowsIcon from "@mui/icons-material/TableRows";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { evaluateFormulaFields } from "../../../../../../../utils/formulaFields";

const isGenericTableTotal = (field) =>
  (field?.generic_table?.field_type || field?.field_type) === "total";

const calculateGenericTableTotal = (formulaFields, valuesByName) => {
  const result = evaluateFormulaFields(formulaFields, valuesByName);

  // Generic-table totals can represent counts or measurements, not just money.
  return Number.isFinite(result) ? String(result) : undefined;
};

const resolveGenericTableTotals = (fields, previousSignatures) => {
  const fieldsByName = new Map(fields.map((field) => [field.name, field]));
  const resolved = new Map();
  const visiting = new Set();

  const resolve = (field) => {
    if (resolved.has(field.name)) return resolved.get(field.name);
    if (visiting.has(field.name)) return null;
    if (
      !isGenericTablePreviewField(field) ||
      !isGenericTableTotal(field) ||
      !Array.isArray(field.formulaFields) ||
      !field.formulaFields.length
    ) {
      return { value: field.value };
    }

    visiting.add(field.name);
    const valuesByName = Object.create(null);
    let hasCircularDependency = false;

    field.formulaFields.forEach((step) => {
      const sourceName = step?.field?.name;
      if (!sourceName) return;
      const sourceField = fieldsByName.get(sourceName);
      const source = sourceField ? resolve(sourceField) : { value: undefined };

      if (source === null) {
        hasCircularDependency = true;
      } else {
        valuesByName[sourceName] = source.value;
      }
    });

    visiting.delete(field.name);
    const signature = JSON.stringify([field.formulaFields, valuesByName]);
    const preserveEditableValue =
      field.isEditable && previousSignatures.get(field.name) === signature;
    const result = hasCircularDependency
      ? null
      : {
          value: preserveEditableValue
            ? field.value
            : calculateGenericTableTotal(field.formulaFields, valuesByName),
          signature,
        };
    resolved.set(field.name, result);
    return result;
  };

  fields.forEach(resolve);
  return resolved;
};

export const isGenericTablePreviewField = (field) =>
  String(field?.field_id || "").toLowerCase() === "generic_table" &&
  Boolean(field?.generic_table?.table_id);

export const groupGenericTableFields = (fields = []) => {
  const groupedTables = new Map();

  fields.filter(isGenericTablePreviewField).forEach((field) => {
    const metadata = field.generic_table;
    const tableId = metadata.table_id;
    const table = groupedTables.get(tableId) || {
      id: tableId,
      label: metadata.table_label || "Generic Table",
      rowMode: metadata.row_mode,
      headerMode: metadata.header_mode,
      configuredRowCount: metadata.row_count,
      maxRowCount: metadata.max_row_count,
      configuredColumnCount: metadata.column_count,
      fields: [],
    };

    table.fields.push(field);
    groupedTables.set(tableId, table);
  });

  return Array.from(groupedTables.values());
};

const buildTableModel = (table) => {
  const cells = new Map();
  let detectedRowCount = 0;
  let detectedColumnCount = 0;

  table.fields.forEach((field) => {
    const rowIndex = Number(field?.generic_table?.row_index);
    const columnIndex = Number(field?.generic_table?.column_index);

    if (!Number.isInteger(rowIndex) || !Number.isInteger(columnIndex)) return;

    cells.set(`${rowIndex}:${columnIndex}`, field);
    detectedRowCount = Math.max(detectedRowCount, rowIndex + 1);
    detectedColumnCount = Math.max(detectedColumnCount, columnIndex + 1);
  });

  const rowCount = Math.max(
    detectedRowCount,
    Number.isInteger(table.configuredRowCount) ? table.configuredRowCount : 0,
  );
  const columnCount = Math.max(
    detectedColumnCount,
    Number.isInteger(table.configuredColumnCount)
      ? table.configuredColumnCount
      : 0,
  );
  const findFieldByPosition = (axis, index) =>
    table.fields.find(
      (field) => Number(field?.generic_table?.[axis]) === index,
    );

  return {
    cells,
    rowCount,
    columnCount,
    columnNames: Array.from({ length: columnCount }, (_, columnIndex) => {
      const field = findFieldByPosition("column_index", columnIndex);
      return field?.generic_table?.column_name || `Column ${columnIndex + 1}`;
    }),
    rowNames: Array.from({ length: rowCount }, (_, rowIndex) => {
      const field = findFieldByPosition("row_index", rowIndex);
      return field?.generic_table?.row_name || `Row ${rowIndex + 1}`;
    }),
  };
};

const isFieldEmpty = (value) =>
  value === undefined || value === null || value === "";

const getGenericTableFieldKey = (field) =>
  String(field?.field_instance_id ?? field?.id ?? field?.name ?? "");

/** Small round step indicator — filled/current/empty, click to jump to that group (row or column). */
const GroupPip = memo(
  ({ index, displayNumber, label, complete, active, onClick }) => (
    <Tooltip title={label} placement="top">
      <Box
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
          }
        }}
        sx={{
          minWidth: 34,
          height: 34,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          flexShrink: 0,
          border: "2px solid",
          borderColor: active
            ? "primary.main"
            : complete
              ? "success.main"
              : "divider",
          bgcolor: active
            ? "primary.main"
            : complete
              ? "rgba(46, 125, 50, 0.12)"
              : "transparent",
          color: active ? "#fff" : complete ? "success.dark" : "text.secondary",
          fontWeight: 700,
          fontSize: "0.8rem",
          transition: "all .15s ease",
          "&:hover": { borderColor: "primary.main" },
        }}
      >
        {complete && !active ? (
          <CheckCircleIcon sx={{ fontSize: 18 }} />
        ) : (
          (displayNumber ?? index + 1)
        )}
      </Box>
    </Tooltip>
  ),
);

const CELL_PX = 1.5;

const TableNameBlock = memo(
  ({ name, axis, index, onJumpToCell, locateFields, onNameChange }) => {
    const [localLabel, setLocalLabel] = useState(name);
    const [isEditingLabel, setIsEditingLabel] = useState(false);
    const isEditingLabelRef = useRef(false);
    const labelEditorRef = useRef(null);

    useEffect(() => {
      if (!locateFields) {
        isEditingLabelRef.current = false;
        setIsEditingLabel(false);
      }
    }, [locateFields]);

    useEffect(() => {
      if (!isEditingLabel) return undefined;

      const id = requestAnimationFrame(() => {
        const el = labelEditorRef.current;
        if (!el) return;
        el.focus();
        const end = el.value.length;
        el.setSelectionRange(end, end);
      });

      return () => cancelAnimationFrame(id);
    }, [isEditingLabel]);

    const finishEditing = (cancel = false) => {
      if (!isEditingLabelRef.current) return;

      isEditingLabelRef.current = false;
      setIsEditingLabel(false);
      const nextName = localLabel.trim();

      if (!cancel && locateFields && nextName && nextName !== name) {
        onNameChange?.(axis, index, nextName);
      }
    };

    const editing = isEditingLabel && locateFields;

    return (
      <Box
        component="th"
        onClick={() => {
          if (!locateFields && !isEditingLabelRef.current) {
            onJumpToCell(
              axis === "row" ? index : null,
              axis === "column" ? index : null,
            );
          }
        }}
        onDoubleClick={(event) => {
          if (!locateFields || !onNameChange || isEditingLabelRef.current)
            return;

          event.preventDefault();
          event.stopPropagation();
          isEditingLabelRef.current = true;
          setLocalLabel(name);
          setIsEditingLabel(true);
        }}
        sx={{
          ...(axis === "row" ? { backgroundColor: "#fff" } : {}),
          cursor: editing ? "text" : "pointer",
          // let the editor fill the whole cell
          ...(editing ? { p: 0, position: "relative" } : {}),
        }}
      >
        {editing ? (
          <TextField
            autoFocus
            fullWidth
            variant="outlined"
            size="small"
            inputRef={labelEditorRef}
            value={localLabel}
            onChange={(event) => setLocalLabel(event.target.value)}
            onFocus={(event) => event.target.select()}
            onClick={(event) => event.stopPropagation()}
            onDoubleClick={(event) => event.stopPropagation()}
            onBlur={() => finishEditing()}
            onKeyDown={(event) => {
              event.stopPropagation();
              if (event.nativeEvent.isComposing) return;

              if (event.key === "Enter" || event.key === "Escape") {
                event.preventDefault();
                finishEditing(event.key === "Escape");
              }
            }}
            inputProps={{ "aria-label": `Edit ${axis} name` }}
            sx={{
              display: "block",
              "& .MuiOutlinedInput-root": {
                borderRadius: 0,
                backgroundColor: "#fff",
                font: "inherit",
                fontWeight: "inherit",
                boxShadow: "inset 0 0 0 1px #0b4a75",
                "& fieldset": { border: "none" },
              },
              "& .MuiOutlinedInput-input": {
                font: "inherit",
                fontWeight: "inherit",
                lineHeight: "inherit",
                py: 1,
                px: CELL_PX,
                height: "auto",
                "&::selection": {
                  backgroundColor: "rgba(11, 74, 117, 0.2)",
                  color: "inherit",
                },
              },
            }}
          />
        ) : (
          name
        )}
      </Box>
    );
  },
);

const ReadOnlyTable = memo(
  ({
    model,
    hasNamedRows,
    onJumpToCell,
    tableId,
    locateFields,
    onNameChange,
  }) => {
    return (
      <Box sx={{ overflowX: "auto", mt: 1.5 }}>
        <Box
          component="table"
          sx={{
            width: "100%",
            minWidth: Math.max(320, model.columnCount * 120),
            borderCollapse: "collapse",
            "& th, & td": {
              border: "1px solid",
              borderColor: "divider",
              p: 0.75,
              fontSize: "0.8rem",
            },
            "& th": {
              backgroundColor: "grey.100",
              fontWeight: 700,
              textAlign: "left",
            },
          }}
        >
          <Box component="thead">
            <Box component="tr">
              {hasNamedRows && <Box component="th">Row</Box>}
              {model.columnNames.map((columnName, columnIndex) => (
                <TableNameBlock
                  key={`${tableId}-rh-${columnIndex}`}
                  name={columnName}
                  axis="column"
                  index={columnIndex}
                  onJumpToCell={onJumpToCell}
                  locateFields={locateFields}
                  onNameChange={onNameChange}
                />
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {Array.from({ length: model.rowCount }, (_, rowIndex) => (
              <Box component="tr" key={`${tableId}-rr-${rowIndex}`}>
                {hasNamedRows && (
                  <TableNameBlock
                    name={model.rowNames[rowIndex]}
                    axis="row"
                    index={rowIndex}
                    onJumpToCell={onJumpToCell}
                    locateFields={locateFields}
                    onNameChange={onNameChange}
                  />
                )}
                {Array.from({ length: model.columnCount }, (_, columnIndex) => {
                  const field = model.cells.get(`${rowIndex}:${columnIndex}`);
                  const empty = isFieldEmpty(field?.value);
                  return (
                    <Box
                      component="td"
                      key={`${tableId}-rc-${rowIndex}-${columnIndex}`}
                      onClick={() => onJumpToCell(rowIndex, columnIndex)}
                      sx={{
                        color: empty ? "text.disabled" : "text.primary",
                        cursor: "pointer",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      {field ? (empty ? "—" : field.value) : ""}
                    </Box>
                  );
                })}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    );
  },
);

const GenericTableCard = memo(
  ({
    table,
    allFields,
    onCellChange,
    onTableNameChange,
    locateFields,
    rowSelectedFieldKeys,
    onToggleRowField,
    onLocateField,
  }) => {
    const baseModel = useMemo(() => buildTableModel(table), [table]);
    const hasNamedRows = table.headerMode === "named_rows";
    const isCustomRowTable =
      table.rowMode === "custom" && table.headerMode === "column_names";
    const parsedMaxRowCount = Number(table.maxRowCount);
    const maxRowCount =
      Number.isInteger(parsedMaxRowCount) && parsedMaxRowCount > 0
        ? parsedMaxRowCount
        : null;

    const [mode, setMode] = useState("row"); // "row" | "column"
    const [currentRow, setCurrentRow] = useState(0);
    const [currentColumn, setCurrentColumn] = useState(0);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [localRows, setLocalRows] = useState([]);
    const [visibleBaseRowIndexes, setVisibleBaseRowIndexes] = useState(() =>
      isCustomRowTable
        ? baseModel.rowCount > 0
          ? [0]
          : []
        : Array.from({ length: baseModel.rowCount }, (_, rowIndex) => rowIndex),
    );
    const nextLocalRowNumberRef = useRef(baseModel.rowCount + 1);
    const fieldRefs = useRef([]);
    const totalDependencySignaturesRef = useRef(new Map());

    const visibleRowNumbers = useMemo(
      () =>
        isCustomRowTable
          ? [
              ...visibleBaseRowIndexes.map((rowIndex) => rowIndex + 1),
              ...localRows.map((localRow) => localRow.displayNumber),
            ]
          : Array.from(
              { length: baseModel.rowCount },
              (_, rowIndex) => rowIndex + 1,
            ),
      [baseModel.rowCount, isCustomRowTable, localRows, visibleBaseRowIndexes],
    );

    const model = useMemo(() => {
      const sourceRowIndexes = table.fields
        .map((field) => Number(field?.generic_table?.row_index))
        .filter(Number.isInteger);
      const templateRowIndex = sourceRowIndexes.length
        ? Math.min(...sourceRowIndexes)
        : 0;
      const templateFieldByColumn = new Map(
        table.fields
          .filter(
            (field) =>
              Number(field?.generic_table?.row_index) === templateRowIndex,
          )
          .map((field) => [Number(field?.generic_table?.column_index), field]),
      );
      const inheritTemplateRowGroup = (field, targetRowKey) => {
        if (field?.generic_table?.row_group_disabled) return "";
        if (field?.row) return field.row;

        const columnIndex = Number(field?.generic_table?.column_index);
        const templateRow = templateFieldByColumn.get(columnIndex)?.row;
        if (!templateRow) return field?.row;

        const [templatePrefix, ...orderParts] = String(templateRow).split("_");
        const targetPrefix = String(targetRowKey).replace(/[^a-zA-Z0-9]/g, "");
        const sourcePrefix = String(templatePrefix).replace(
          /[^a-zA-Z0-9]/g,
          "",
        );
        const order = orderParts.join("_") || columnIndex + 1;

        return `genericrow${targetPrefix}${sourcePrefix}_${order}`;
      };

      const visibleBaseFields = table.fields.flatMap((field) => {
        const sourceRowIndex = Number(field?.generic_table?.row_index);
        const visibleRowIndex = visibleBaseRowIndexes.indexOf(sourceRowIndex);

        if (visibleRowIndex === -1) return [];

        return {
          ...field,
          row: inheritTemplateRowGroup(
            field,
            `${table.id}-source-row-${sourceRowIndex}`,
          ),
          generic_table: {
            ...field.generic_table,
            row_index: visibleRowIndex,
          },
        };
      });
      const visibleLocalFields = localRows.flatMap((localRow, localRowIndex) =>
        localRow.fields.map((field) => ({
          ...field,
          row: inheritTemplateRowGroup(field, localRow.id),
          generic_table: {
            ...field.generic_table,
            row_index: visibleBaseRowIndexes.length + localRowIndex,
          },
        })),
      );

      return buildTableModel({
        ...table,
        configuredRowCount: visibleBaseRowIndexes.length + localRows.length,
        fields: [...visibleBaseFields, ...visibleLocalFields],
      });
    }, [localRows, table, visibleBaseRowIndexes]);

    useEffect(() => {
      const visibleFields = Array.from(model.cells.values());
      const fieldsByName = new Map(
        allFields.map((field) => [field.name, field]),
      );
      visibleFields.forEach((field) => fieldsByName.set(field.name, field));
      const totals = resolveGenericTableTotals(
        Array.from(fieldsByName.values()),
        totalDependencySignaturesRef.current,
      );
      const localUpdates = new Map();
      const nextSignatures = new Map();
      totals.forEach((total, name) => {
        if (total?.signature) nextSignatures.set(name, total.signature);
      });

      visibleFields.forEach((field) => {
        const total = totals.get(field.name);
        if (!total?.signature || total.value === undefined) return;

        const dependenciesUnchanged =
          totalDependencySignaturesRef.current.get(field.name) ===
          total.signature;
        if (
          field.value === total.value ||
          (field.isEditable && dependenciesUnchanged)
        ) {
          return;
        }

        if (field.isLocalPreviewField) {
          localUpdates.set(field.localPreviewId, total.value);
        } else {
          onCellChange?.(field, total.value);
        }
      });
      totalDependencySignaturesRef.current = nextSignatures;

      if (localUpdates.size) {
        setLocalRows((currentRows) =>
          currentRows.map((localRow) => ({
            ...localRow,
            fields: localRow.fields.map((field) =>
              localUpdates.has(field.localPreviewId)
                ? { ...field, value: localUpdates.get(field.localPreviewId) }
                : field,
            ),
          })),
        );
      }
    }, [allFields, model, onCellChange]);

    const groupCount = mode === "row" ? model.rowCount : model.columnCount;
    const itemCount = mode === "row" ? model.columnCount : model.rowCount;
    const currentGroup = mode === "row" ? currentRow : currentColumn;

    const getField = (groupIndex, itemIndex) =>
      mode === "row"
        ? model.cells.get(`${groupIndex}:${itemIndex}`)
        : model.cells.get(`${itemIndex}:${groupIndex}`);

    const getGroupLabel = (groupIndex) =>
      mode === "row"
        ? hasNamedRows
          ? model.rowNames[groupIndex]
          : `Row ${visibleRowNumbers[groupIndex] ?? groupIndex + 1}`
        : model.columnNames[groupIndex];

    const getItemLabel = (itemIndex) =>
      mode === "row"
        ? model.columnNames[itemIndex]
        : hasNamedRows
          ? model.rowNames[itemIndex]
          : `Row ${visibleRowNumbers[itemIndex] ?? itemIndex + 1}`;

    const { filledCount, totalCount } = useMemo(() => {
      let filled = 0;
      model.cells.forEach((field) => {
        if (!isFieldEmpty(field?.value)) filled += 1;
      });
      return { filledCount: filled, totalCount: model.cells.size };
    }, [model.cells]);

    const groupComplete = useMemo(
      () =>
        Array.from({ length: groupCount }, (_, groupIndex) => {
          let hasAnyField = false;
          for (let itemIndex = 0; itemIndex < itemCount; itemIndex += 1) {
            const field = getField(groupIndex, itemIndex);
            if (!field) continue;
            hasAnyField = true;
            if (isFieldEmpty(field.value)) return false;
          }
          return hasAnyField;
        }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [mode, model, groupCount, itemCount],
    );

    if (!model.rowCount || !model.columnCount) return null;

    const setCurrentGroup = (index) => {
      const clamped = Math.max(0, Math.min(index, groupCount - 1));
      if (mode === "row") setCurrentRow(clamped);
      else setCurrentColumn(clamped);
    };

    // const goNextIncomplete = () => {
    //   for (let step = 1; step <= groupCount; step += 1) {
    //     const candidate = (currentGroup + step) % groupCount;
    //     if (!groupComplete[candidate]) {
    //       setCurrentGroup(candidate);
    //       return;
    //     }
    //   }
    // };

    const handleItemEnter = (itemIndex) => {
      if (itemIndex < itemCount - 1) {
        fieldRefs.current[itemIndex + 1]?.focus();
      } else if (currentGroup < groupCount - 1) {
        setCurrentGroup(currentGroup + 1);
      }
    };

    const handleCellChange = (field, value) => {
      if (!field?.isLocalPreviewField) {
        onCellChange?.(field, value);
        return;
      }

      setLocalRows((currentRows) =>
        currentRows.map((localRow) => ({
          ...localRow,
          fields: localRow.fields.map((localField) =>
            localField.localPreviewId === field.localPreviewId
              ? { ...localField, value }
              : localField,
          ),
        })),
      );
    };

    const handleTableNameChange = (axis, index, nextName) => {
      if (!locateFields || !onTableNameChange) return;

      const nameKey = axis === "row" ? "row_name" : "column_name";
      const sourceIndex = axis === "row" ? visibleBaseRowIndexes[index] : index;

      if (sourceIndex !== undefined) {
        onTableNameChange(table.id, axis, sourceIndex, nextName);
      }

      // Preview-only rows keep their own metadata outside the document fields.
      setLocalRows((currentRows) =>
        currentRows.map((localRow, localRowIndex) => {
          if (
            axis === "row" &&
            visibleBaseRowIndexes.length + localRowIndex !== index
          ) {
            return localRow;
          }

          return {
            ...localRow,
            fields: localRow.fields.map((field) =>
              axis === "row" ||
              Number(field.generic_table?.column_index) === index
                ? {
                    ...field,
                    generic_table: {
                      ...field.generic_table,
                      [nameKey]: nextName,
                    },
                  }
                : field,
            ),
          };
        }),
      );
    };

    const handleAddRow = () => {
      if (!isCustomRowTable || (maxRowCount && model.rowCount >= maxRowCount)) {
        return;
      }

      const nextBaseRowIndex = Array.from(
        { length: baseModel.rowCount },
        (_, rowIndex) => rowIndex,
      ).find((rowIndex) => !visibleBaseRowIndexes.includes(rowIndex));

      if (nextBaseRowIndex !== undefined) {
        setVisibleBaseRowIndexes((currentIndexes) => [
          ...currentIndexes,
          nextBaseRowIndex,
        ]);
        setMode("row");
        setCurrentRow(model.rowCount);
        setReviewOpen(false);
        return;
      }

      const rowIndex = model.rowCount;
      const displayNumber = nextLocalRowNumberRef.current;
      nextLocalRowNumberRef.current += 1;
      const localRowId = `${table.id}-local-row-${Date.now()}-${rowIndex}`;
      const fields = Array.from(
        { length: baseModel.columnCount },
        (_, columnIndex) => {
          const sourceField = table.fields.find(
            (field) =>
              Number(field?.generic_table?.column_index) === columnIndex,
          );
          const columnName =
            sourceField?.generic_table?.column_name ||
            baseModel.columnNames[columnIndex];
          const fieldType =
            sourceField?.generic_table?.column_type ||
            sourceField?.generic_table?.field_type ||
            sourceField?.field_type ||
            "text";

          return {
            field_id: "generic_table",
            field_type: fieldType,
            name: `${localRowId}-column-${columnIndex}`,
            value: "",
            isLocalPreviewField: true,
            localPreviewId: `${localRowId}:${columnIndex}`,
            generic_table: {
              table_id: table.id,
              table_label: table.label,
              row_mode: table.rowMode,
              header_mode: table.headerMode,
              row_count: baseModel.rowCount,
              max_row_count: maxRowCount,
              column_count: baseModel.columnCount,
              row_index: rowIndex,
              column_index: columnIndex,
              row_name: null,
              column_name: columnName,
              field_type: fieldType,
              column_type: fieldType,
              column_total_sources:
                sourceField?.generic_table?.column_total_sources || [],
            },
          };
        },
      );

      fields.forEach((field) => {
        if (!isGenericTableTotal(field)) return;
        const sourceColumns = field.generic_table.column_total_sources;
        if (!Array.isArray(sourceColumns)) return;

        field.formulaFields = sourceColumns
          .filter(
            (columnIndex) =>
              Number.isInteger(columnIndex) &&
              fields[columnIndex] &&
              !isGenericTableTotal(fields[columnIndex]),
          )
          .map((columnIndex, index) => ({
            field: {
              name: fields[columnIndex].name,
              field_label: fields[columnIndex].generic_table.column_name,
            },
            ...(index > 0 ? { operator: { value: "+", label: "+ Add" } } : {}),
          }));
      });

      setLocalRows((currentRows) => [
        ...currentRows,
        { id: localRowId, displayNumber, fields },
      ]);
      setMode("row");
      setCurrentRow(rowIndex);
      setReviewOpen(false);
    };

    const handleRemoveCurrentRow = () => {
      if (!isCustomRowTable || model.rowCount <= 1) return;

      if (currentRow < visibleBaseRowIndexes.length) {
        const sourceRowIndex = visibleBaseRowIndexes[currentRow];

        table.fields
          .filter(
            (field) =>
              Number(field?.generic_table?.row_index) === sourceRowIndex,
          )
          .forEach((field) => onCellChange?.(field, ""));

        setVisibleBaseRowIndexes((currentIndexes) =>
          currentIndexes.filter((_, index) => index !== currentRow),
        );
      } else {
        const localRowIndex = currentRow - visibleBaseRowIndexes.length;
        setLocalRows((currentRows) =>
          currentRows.filter((_, index) => index !== localRowIndex),
        );
      }

      setCurrentRow(Math.max(0, Math.min(currentRow, model.rowCount - 2)));
      setReviewOpen(false);
    };

    const handleJumpToCell = (rowIndex, columnIndex) => {
      setReviewOpen(false);
      if (mode === "row") {
        setCurrentRow(rowIndex ?? currentRow);
      } else {
        setCurrentColumn(columnIndex ?? currentColumn);
      }
    };

    const groupItems = Array.from({ length: itemCount }, (_, itemIndex) =>
      getField(currentGroup, itemIndex),
    );
    const groupedItemUnits = (() => {
      if (mode !== "row") {
        return groupItems.map((field, itemIndex) => ({
          id: `${table.id}-item-${itemIndex}`,
          items: [{ field, itemIndex }],
        }));
      }

      const units = [];
      const unitByRowPrefix = new Map();

      groupItems.forEach((field, itemIndex) => {
        const rowPrefix = field?.row ? String(field.row).split("_")[0] : "";

        if (!rowPrefix) {
          units.push({
            id: `${table.id}-item-${itemIndex}`,
            items: [{ field, itemIndex }],
          });
          return;
        }

        const existingUnit = unitByRowPrefix.get(rowPrefix);
        const groupedItem = { field, itemIndex };

        if (existingUnit) {
          existingUnit.items.push(groupedItem);
          return;
        }

        const unit = {
          id: `${table.id}-row-group-${rowPrefix}`,
          items: [groupedItem],
        };
        unitByRowPrefix.set(rowPrefix, unit);
        units.push(unit);
      });

      return units;
    })();

    const allFilled = totalCount > 0 && filledCount === totalCount;
    const isLastGroup = currentGroup === groupCount - 1;
    const canAddRow =
      isCustomRowTable && (!maxRowCount || model.rowCount < maxRowCount);
    const canRemoveCurrentRow =
      isCustomRowTable && mode === "row" && model.rowCount > 1;

    return (
      <Box
        sx={{
          backgroundColor: "#fff",
          border: "2px solid #d3d3d3",
          borderRadius: "15px",
          px: 2,
          py: 1.5,
          boxShadow:
            "0px 4px 6px rgba(0, 0, 0, 0.4), 0px 1px 3px rgba(0, 0, 0, 0.08)",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          sx={{ mb: 1 }}
        >
          <Typography variant="h6" color="primary">
            {table.label}
          </Typography>

          <Stack direction="row" alignItems="center" gap={1}>
            <ToggleButtonGroup
              size="small"
              value={mode}
              exclusive
              onChange={(event, nextMode) => {
                if (nextMode) setMode(nextMode);
              }}
            >
              <ToggleButton value="row" sx={{ px: 1.25, gap: 0.5 }}>
                <TableRowsIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption" fontWeight={600}>
                  By row
                </Typography>
              </ToggleButton>
              <ToggleButton
                value="column"
                sx={{ px: 1.25, gap: 0.5 }}
                disabled={table.rowMode === "custom"}
              >
                <ViewColumnIcon sx={{ fontSize: 18 }} />
                <Typography variant="caption" fontWeight={600}>
                  By column
                </Typography>
              </ToggleButton>
            </ToggleButtonGroup>
            {totalCount > 0 && (
              <Chip
                size="small"
                label={`${filledCount}/${totalCount} filled`}
                color={allFilled ? "success" : "default"}
                variant={allFilled ? "filled" : "outlined"}
              />
            )}
          </Stack>
        </Stack>

        <LinearProgress
          variant="determinate"
          value={totalCount ? (filledCount / totalCount) * 100 : 0}
          sx={{ height: 6, borderRadius: 3, mb: 1.5 }}
        />

        <Box
          sx={{ display: "flex", gap: 1, overflowX: "auto", pb: 1, mb: 0.5 }}
        >
          {Array.from({ length: groupCount }, (_, groupIndex) => (
            <GroupPip
              key={`${table.id}-${mode}-pip-${groupIndex}`}
              index={groupIndex}
              displayNumber={
                mode === "row" ? visibleRowNumbers[groupIndex] : groupIndex + 1
              }
              label={getGroupLabel(groupIndex)}
              complete={groupComplete[groupIndex]}
              active={groupIndex === currentGroup}
              onClick={() => setCurrentGroup(groupIndex)}
            />
          ))}
        </Box>

        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            p: 2,
            mb: 1.5,
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="baseline"
            sx={{ mb: 1.5 }}
          >
            <Typography variant="subtitle1" fontWeight={700}>
              {getGroupLabel(currentGroup)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {mode === "row" ? "Row" : "Column"} {currentGroup + 1} of{" "}
              {groupCount}
            </Typography>
          </Stack>

          <Stack spacing={2}>
            {groupedItemUnits.map((unit) => (
              <Stack
                key={`${table.id}-${mode}-${currentGroup}-${unit.id}`}
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems="stretch"
              >
                {unit.items.map(({ field, itemIndex }) =>
                  field ? (
                    <TextField
                      key={`${table.id}-wiz-${mode}-${currentGroup}-${itemIndex}`}
                      inputRef={(el) => {
                        fieldRefs.current[itemIndex] = el;
                      }}
                      label={getItemLabel(itemIndex)}
                      fullWidth
                      variant="filled"
                      type={field.field_type === "number" ? "number" : "text"}
                      value={field.value ?? ""}
                      inputProps={{
                        readOnly:
                          locateFields ||
                          (isGenericTableTotal(field) && !field.isEditable),
                      }}
                      onChange={(event) =>
                        !locateFields &&
                        (!isGenericTableTotal(field) || field.isEditable) &&
                        handleCellChange(field, event.target.value)
                      }
                      onClick={(event) => {
                        if (field.isLocalPreviewField) return;

                        if (event.ctrlKey || event.metaKey) {
                          event.preventDefault();
                          event.stopPropagation();
                          onToggleRowField?.(field);
                          return;
                        }

                        if (!locateFields) return;

                        event.preventDefault();
                        event.stopPropagation();
                        onLocateField?.(field);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          handleItemEnter(itemIndex);
                        }
                      }}
                      autoFocus={itemIndex === 0}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        outline: rowSelectedFieldKeys?.has(
                          getGenericTableFieldKey(field),
                        )
                          ? "2px solid"
                          : "none",
                        outlineColor: "primary.main",
                        outlineOffset: 2,
                        backgroundColor: rowSelectedFieldKeys?.has(
                          getGenericTableFieldKey(field),
                        )
                          ? "rgba(25, 118, 210, 0.08)"
                          : "transparent",
                      }}
                    />
                  ) : null,
                )}
              </Stack>
            ))}
          </Stack>

          {isCustomRowTable && (
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              sx={{ mt: 1.5 }}
            >
              {canRemoveCurrentRow && (
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={handleRemoveCurrentRow}
                >
                  Remove row
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon />}
                disabled={!canAddRow}
                onClick={handleAddRow}
              >
                Add row
              </Button>
            </Stack>
          )}
        </Box>

        <Stack direction="row" spacing={1} justifyContent="space-between">
          <Button
            startIcon={<ChevronLeftIcon />}
            disabled={currentGroup === 0}
            onClick={() => setCurrentGroup(currentGroup - 1)}
          >
            Back
          </Button>

          {/* <Button size="small" onClick={goNextIncomplete} disabled={allFilled}>
          Jump to next empty
        </Button> */}

          {!isLastGroup ? (
            <Button
              endIcon={<ChevronRightIcon />}
              variant="contained"
              onClick={() => setCurrentGroup(currentGroup + 1)}
            >
              Next
            </Button>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={() => setReviewOpen(true)}
            >
              Review all
            </Button>
          )}
        </Stack>

        <Box sx={{ mt: 1.5 }}>
          <Button
            size="small"
            onClick={() => setReviewOpen((v) => !v)}
            endIcon={reviewOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          >
            {reviewOpen ? "Hide full table" : "View full table"}
          </Button>
          <Collapse in={reviewOpen}>
            <ReadOnlyTable
              model={model}
              hasNamedRows={hasNamedRows}
              tableId={table.id}
              onJumpToCell={handleJumpToCell}
              locateFields={locateFields}
              onNameChange={
                onTableNameChange ? handleTableNameChange : undefined
              }
            />
          </Collapse>
        </Box>
      </Box>
    );
  },
);

const GenericTablePreview = memo(
  ({
    fields = [],
    onCellChange,
    onTableNameChange,
    locateFields = false,
    rowSelectedFieldKeys,
    onToggleRowField,
    onLocateField,
  }) => {
    const tables = useMemo(() => groupGenericTableFields(fields), [fields]);

    if (!tables.length) return null;

    return (
      <Stack spacing={1.5}>
        {tables.map((table) => (
          <GenericTableCard
            key={table.id}
            table={table}
            allFields={fields}
            onCellChange={onCellChange}
            onTableNameChange={onTableNameChange}
            locateFields={locateFields}
            rowSelectedFieldKeys={rowSelectedFieldKeys}
            onToggleRowField={onToggleRowField}
            onLocateField={onLocateField}
          />
        ))}
      </Stack>
    );
  },
);

export default GenericTablePreview;
